import { Controller } from 'react-hook-form';
import { useSendNotificationWithForm } from '../hooks/useSendNotificationWithForm';
import { useFormNavigation, useConfirmationDialog } from '@/shared/hooks';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import MarkdownEditor from '@/components/forms/raw-inputs/markdown-editor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/shared/utils';
import { handleInvalidSubmit } from '@/shared/utils';

export default function SendNotificationPage() {
  const { form, onSubmit, onCancel, isLoading, isSubmitting, isDirty } = useSendNotificationWithForm();

  // Navigation blocking
  const navigationDialog = useConfirmationDialog({
    onConfirm: () => blocker.proceed?.(),
    onCancel: () => blocker.reset?.(),
  });

  const { blocker } = useFormNavigation({
    isDirty,
    onNavigationAttempt: navigationDialog.show,
  });

  // Submission confirmation
  const submitDialog = useConfirmationDialog({
    onConfirm: form.handleSubmit(async (data) => {
      console.log('[SendNotification] Dialog confirmed, submitting data:', data);
      await onSubmit(data);
      // Al doilea argument nu e optional in practica: fara el, un submit respins de Zod NU
      // executa nimic — dialogul se inchide si utilizatorul ramane fara niciun semnal.
    }, () => handleInvalidSubmit()),
  });

  const isActionsDisabled = isLoading || isSubmitting;

  return (
    <div className="flex items-center justify-center min-h-dvh p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader>
          <CardTitle>Trimitere Notificare Bulk</CardTitle>
        </CardHeader>

        {!isLoading && (
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitDialog.show();
              }}
            >
              <CardContent className="space-y-4">
                {/* Send to Teachers Checkbox */}
                <FormField
                  control={form.control}
                  name="sendToTeachers"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isActionsDisabled}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Notificare destinata profesorilor?</FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                {/* Send to Pupils Checkbox */}
                <FormField
                  control={form.control}
                  name="sendToPupils"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isActionsDisabled}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Notificare destinata elevilor?</FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                {/* Subject Field */}
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subiectul Notificarii (Obligatoriu)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isActionsDisabled}
                          placeholder="Introduceți subiectul..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Content Field - Markdown Editor */}
                <Controller
                  control={form.control}
                  name="content"
                  render={({ field, fieldState }) => (
                    <div
                      className={cn(
                        'rounded-lg border-2 px-4 pb-4',
                        fieldState.error ? 'border-error' : 'border-input'
                      )}
                    >
                      <span className="block w-full text-center font-bold text-muted-foreground py-2">
                        Conținutul notificării (obligatoriu)
                      </span>
                      <div className="markdown max-h-96 overflow-y-scroll pr-4">
                        <MarkdownEditor
                          name={field.name}
                          changeContent={(text) => field.onChange(text)}
                          content={field.value}
                          componentKey="content"
                        />
                      </div>
                      {fieldState.error && (
                        <p className="text-sm font-medium text-destructive mt-2">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </CardContent>

              <CardFooter className="justify-end gap-4">
                <Button
                  type="button"
                  variant="destructive"
                  size="lg"
                  onClick={onCancel}
                  disabled={isActionsDisabled}
                >
                  Anulare mesaj
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  disabled={!isDirty || isActionsDisabled}
                >
                  {isSubmitting ? 'Se trimite...' : 'Trimitere mesaj'}
                </Button>
              </CardFooter>
            </form>
          </Form>
        )}
      </Card>

      {/* Navigation confirmation */}
      <ConfirmationDialog
        isOpen={navigationDialog.isOpen}
        onClose={navigationDialog.handleCancel}
        onConfirm={navigationDialog.handleConfirm}
        title="Modificări nesalvate"
        description="Aveți modificări nesalvate. Doriți să părăsiți pagina?"
        confirmText="Părăsește pagina"
        cancelText="Rămâi pe pagină"
        variant="destructive"
      />

      {/* Submission confirmation */}
      <ConfirmationDialog
        isOpen={submitDialog.isOpen}
        onClose={submitDialog.handleCancel}
        onConfirm={submitDialog.handleConfirm}
        title="Confirmare trimitere"
        description="Doriți să trimiteți notificarea?"
        confirmText="Trimite"
        cancelText="Anulare"
        loading={isSubmitting}
      />
    </div>
  );
}
